/**
 * orgScope -- which customer (org) an in-page list filter is allowed to show (OP #1599).
 *
 * THE BUG THIS EXISTS FOR. An msp_admin picked header Customer=GTK, Site=All sites, and the
 * Devices page listed 32 devices from EVERY customer (including 903cb32d3021 at Jedi's "Jedi
 * Office") while the in-page customer dropdown read "All customers". The page derived its
 * customer filter from the `?org=` URL param alone and never looked at the header, so "All
 * sites" silently meant "all sites of all customers". Not a cross-tenant leak (an msp_admin may
 * see every org), but the page showed a different scope than the header claimed.
 *
 * THE RULE, in one place so every list follows it the same way:
 *   - Header has a customer selected  -> the list is pinned to it. The in-page customer
 *     control shows that customer and is LOCKED; a stale/hand-edited `?org=` that names a
 *     different customer is ignored (the header is the scope the operator can see).
 *   - Header has NO customer (`currentOrgId === null`) -> the only state in which "All
 *     customers" is legitimate. The URL param (if any) drives the filter, unlocked.
 *
 * Today the org-scoped routes all live under /o/:orgId and session.hydrate() auto-selects the
 * first org, so the header is effectively never null on these pages; the null branch is kept
 * so a future org-less "All customers" view inherits the right behaviour instead of a pinned
 * filter on a null id.
 *
 * Pure (no React, no store, no "@/" alias imports) so `npm run test:unit` can exercise it
 * directly; the React wrapper is hooks/useEffectiveOrgFilter.ts.
 */

/** What a list page should do with its customer filter. */
export interface EffectiveOrgFilter {
  /** The org id to filter by, or "" for "All customers" (only possible when unlocked). */
  orgFilter: string;
  /** True when the header pins the customer: the in-page control must not offer others. */
  locked: boolean;
}

/**
 * Resolve the effective customer filter for a list page.
 *
 * @param headerOrgId  `useSession(s => s.currentOrgId)` -- the ScopeBar's customer, or null.
 * @param urlOrg       the page's own `?org=` param (null/"" when absent).
 */
export function effectiveOrgFilter(
  headerOrgId: string | number | null | undefined,
  urlOrg: string | null | undefined,
): EffectiveOrgFilter {
  // Header customer wins outright. String() because the store carries ids as strings but
  // some callers (and the /me payload) hand numbers around; the filter compares as strings.
  if (headerOrgId != null && String(headerOrgId) !== "") {
    return { orgFilter: String(headerOrgId), locked: true };
  }
  // Header on "All customers": the page's own filter (if any) is the only narrowing.
  return { orgFilter: urlOrg ?? "", locked: false };
}
